import type { Session } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { PREVIEW, PREVIEW_SIGNED_OUT } from "./preview";
import { supabase } from "./supabase";

const LAST_USER = "ops-portal:last-user";

/** Minimal view of the signed-in user the shell needs. */
export interface SignedInUser {
  id: string;
  email: string;
}

/**
 * Current Supabase session: `undefined` while loading, `null` when signed out.
 * In DEV preview mode a stand-in session is returned without touching Supabase.
 */
export function useSession(): SignedInUser | null | undefined {
  const [user, setUser] = useState<SignedInUser | null | undefined>(undefined);

  useEffect(() => {
    if (PREVIEW) {
      if (PREVIEW_SIGNED_OUT) setUser(null);
      else void import("../dev/fixtures").then(({ profile }) => setUser({ id: profile.id, email: profile.email ?? "" }));
      return;
    }
    const toUser = (s: Session | null) => (s ? { id: s.user.id, email: s.user.email ?? "" } : null);
    // With no signal an expired session can't be refreshed: stay signed in as the last user, so the job packs
    // saved on the phone still open. The session refreshes once the phone is back online.
    const remember = (u: SignedInUser | null) => {
      try {
        if (u) window.localStorage.setItem(LAST_USER, JSON.stringify(u));
      } catch {
        /* storage blocked */
      }
      return u;
    };
    const lastUser = (): SignedInUser | null => {
      try {
        return navigator.onLine === false ? (JSON.parse(window.localStorage.getItem(LAST_USER) ?? "null") as SignedInUser | null) : null;
      } catch {
        return null;
      }
    };
    supabase.auth.getSession().then(({ data }) => setUser(remember(toUser(data.session)) ?? lastUser()), () => setUser(lastUser()));
    const { data } = supabase.auth.onAuthStateChange((e, s) => setUser(remember(toUser(s)) ?? (e === "SIGNED_OUT" ? null : lastUser())));
    return () => data.subscription.unsubscribe();
  }, []);

  return user;
}

/** Magic link sign-in. Accounts are invited by a director; sign-up is disabled. */
export async function sendMagicLink(email: string): Promise<{ error: string | null }> {
  if (PREVIEW) {
    await new Promise((r) => setTimeout(r, 600));
    return { error: email.startsWith("error") ? "Signups not allowed for otp" : null };
  }
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: false, emailRedirectTo: window.location.origin },
  });
  return { error: error ? error.message : null };
}

export async function signOut(): Promise<void> {
  if (PREVIEW) {
    window.location.search = "?preview=login";
    return;
  }
  try {
    window.localStorage.removeItem(LAST_USER);
  } catch {
    /* storage blocked */
  }
  await supabase.auth.signOut();
}
